import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';

import EyePasswordImgSrc from '@/assets/icons/eye.svg';
import EyePasswordCrossedSrc from '@/assets/icons/eye-crossed.svg';
import {
  createRoadmapsPopupVisible,
  deleteRoadmapsPopupVisible,
  getRoadmapsPopupVisible,
} from '@/features/canvas/api';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

interface LinkedHideFieldIconProps {
  capexField: components['schemas']['InitiativeField'];
  opexField: components['schemas']['InitiativeField'];
}

const LinkedHideFieldIcon = ({ capexField, opexField }: LinkedHideFieldIconProps) => {
  const { popupVisible, roadmap } = useEditorContext();
  const [visible, setVisible] = useState<boolean>();

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Both CapEx and OpEx should have the same visibility state (linked)
    const isCapexVisible = popupVisible.some(
      f => f.title === capexField.title && f.type === capexField.type
    );
    const isOpexVisible = popupVisible.some(
      f => f.title === opexField.title && f.type === opexField.type
    );

    // If either is visible, consider both visible (linked behavior)
    // In practice, they should always be in sync due to the linked updates
    setVisible(isCapexVisible || isOpexVisible);
  }, [capexField.title, capexField.type, opexField.title, opexField.type, popupVisible]);

  const handleVisibility = async () => {
    // Find if either field is currently visible
    const capexEntry = popupVisible.find(
      f => f.title === capexField.title && f.type === capexField.type
    );
    const opexEntry = popupVisible.find(
      f => f.title === opexField.title && f.type === opexField.type
    );

    const isCurrentlyVisible = !!capexEntry || !!opexEntry;

    if (isCurrentlyVisible) {
      // Hide both fields - delete both entries if they exist
      if (capexEntry) {
        await deleteRoadmapsPopupVisible({ roadmapId: capexEntry.id! });
      }
      if (opexEntry) {
        await deleteRoadmapsPopupVisible({ roadmapId: opexEntry.id! });
      }
    } else {
      // Show both fields - create both entries
      await createRoadmapsPopupVisible({
        roadmapId: roadmap.id!,
        newPopupVisible: {
          id: capexField.id,
          title: capexField.title!,
          type: capexField.type,
        },
      });
      await createRoadmapsPopupVisible({
        roadmapId: roadmap.id!,
        newPopupVisible: {
          id: opexField.id,
          title: opexField.title!,
          type: opexField.type,
        },
      });
    }

    await queryClient.invalidateQueries([getRoadmapsPopupVisible.name, roadmap.id]);
    setVisible(!visible);
  };

  return (
    <IconButton aria-label="toggle-hide" size="small" onClick={handleVisibility}>
      <Tooltip
        title={
          visible
            ? t('editor.sidebar.initiative.budget.hide_cap_ex')
            : t('editor.sidebar.initiative.budget.show_cap_ex')
        }
        placement="top"
      >
        <StyledImage src={visible ? EyePasswordImgSrc : EyePasswordCrossedSrc} />
      </Tooltip>
    </IconButton>
  );
};

export { LinkedHideFieldIcon };

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  filter: ${({ theme }) => theme.palette.brand.textSecondaryFilter};
  &:hover {
    filter: ${({ theme }) => theme.palette.brand.buttonPrimaryFilterHover};
  }
`;
